<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import CountryAgentBots from './CountryAgentBots.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
    CountryAgentBots,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      countryAgentBots: {},
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    storedCountryAgentBots() {
      return this.$store.getters['agentBots/getCountryAgentBots'](
        this.inbox.id
      );
    },
    // A marketplace inbox serves several countries, and each can have its own
    // bot instead of the default one.
    allowedCountryAgentBots() {
      return [INBOX_TYPES.KAUFLAND, INBOX_TYPES.ALLEGRO].includes(
        this.inbox.channel_type
      );
    },
    // Disconnecting removes the default bot and the country bots with it.
    disconnectLabel() {
      return this.allowedCountryAgentBots
        ? this.$t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.DISCONNECT_ALL')
        : this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT');
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
    storedCountryAgentBots: {
      handler(value) {
        this.countryAgentBots = { ...value };
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          // Without a default bot the inbox has no bot, so the country bots go too.
          countryAgentBots:
            this.allowedCountryAgentBots && this.selectedAgentBotId
              ? this.countryAgentBots
              : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div>
          <label>
            <select v-model="selectedAgentBotId">
              <option value="" disabled selected>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agentBot in agentBots"
                :key="agentBot.id"
                :value="agentBot.id"
              >
                <template v-if="agentBot.description">
                  {{
                    $t('AGENT_BOTS.BOT_CONFIGURATION.OPTION', {
                      name: agentBot.name,
                      description: agentBot.description,
                    })
                  }}
                </template>
                <template v-else>
                  {{ agentBot.name }}
                </template>
              </option>
            </select>
          </label>
          <div v-if="allowedCountryAgentBots" class="mb-6">
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.TITLE') }}
            </p>
            <p class="mb-3 text-sm text-n-slate-11">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.COUNTRY_BOTS.DESC') }}
            </p>
            <CountryAgentBots
              :country-bots="countryAgentBots"
              :agent-bots="agentBots"
              :disabled="!selectedAgentBotId"
              @update="value => (countryAgentBots = value)"
            />
          </div>
          <div class="button-container space-x-2">
            <NextButton
              type="submit"
              :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :is-loading="uiFlags.isSettingAgentBot"
            />
            <NextButton
              type="button"
              :disabled="!selectedAgentBotId"
              :is-loading="uiFlags.isDisconnecting"
              faded
              ruby
              @click="disconnectBot"
            >
              {{ disconnectLabel }}
            </NextButton>
          </div>
        </div>
      </SettingsSection>
    </form>
  </div>
</template>
